import { useState } from "react"
import type { IAccountPayableBill } from "@apis/accounts/types"
import { formatDate } from "@utils/formatters"
import { DataTable } from "@components/DataTable/DataTable"
import { historyColumns } from "./tableColumnSchema"
import { Button } from "@components/ui/button"
import ErrorMessage from "@components/ErrorMessage"
import { ReissueInformationForm } from "./ReissueInformationForm"
import { useAccount } from "@apis/accounts/useAccounts"

type TRewardCheckItemProps = { payable_bill: IAccountPayableBill }

export const RewardCheckItemDetail = ({
  payable_bill: payable_bill,
}: TRewardCheckItemProps) => {
  const [reissuing, setReissuing] = useState(false)
  const { data: accountData } = useAccount(payable_bill.account_id.toString())

  return (
    <div className="tw:flex tw:flex-col tw:space-y-4 tw:bg-white tw:p-4">
      <div className="tw:grid tw:grid-cols-2 tw:gap-4">
        <div className="tw:space-y-1 tw:font-semibold">
          <p>
            Invoice #:{" "}
            <span className="tw:font-normal">
              {payable_bill.invoice_number}
            </span>
          </p>
          <p>
            Vendor:{" "}
            <span className="tw:font-normal">
              {payable_bill.vendor.vendor_name}
            </span>
          </p>
          <p>
            Memo:{" "}
            <span className="tw:font-normal">{payable_bill.check_memo}</span>
          </p>
          <p>
            Check #:{" "}
            <span className="tw:font-normal">{payable_bill.check_number}</span>
          </p>
        </div>
        <div className="tw:space-y-1 tw:font-semibold">
          <p>
            Check Date:{" "}
            <span className="tw:font-normal">
              {formatDate(payable_bill.check_dt, "M/dd/yy")}
            </span>
          </p>
          <p>
            Est. Delivery Date:{" "}
            <span className="tw:font-normal">
              {formatDate(payable_bill.check_arrives_by_dt, "M/dd/yy")}
            </span>
          </p>
          <p>
            Cleared Date:{" "}
            <span className="tw:font-normal">
              {formatDate(payable_bill.check_cleared_dt, "M/dd/yy")}
            </span>
          </p>
          <p>
            Expiration Date:{" "}
            <span className="tw:font-normal">
              {formatDate(payable_bill.check_expiration_dt, "M/dd/yy")}
            </span>
          </p>
        </div>
      </div>
      {payable_bill.reissuable && (
        <div className="tw:self-end">
          <Button variant={"outline"} onClick={() => setReissuing(!reissuing)}>
            {reissuing ? "Cancel" : "Update Info and Reissue Check"}
          </Button>
        </div>
      )}
      {reissuing && (
        <ReissueInformationForm
          account_id={payable_bill.account_id.toString()}
          account_payable_bill_id={payable_bill.account_payable_bill_id}
          address_guid={accountData?.address_guid as string}
          onSubmitSuccess={() => setReissuing(false)}
        />
      )}
      {payable_bill.check_error_message && (
        <ErrorMessage
          message={`Error: ${payable_bill.check_error_message}`}
          variant="destructive-secondary"
        />
      )}
      <DataTable columns={historyColumns} data={payable_bill.histories} />
    </div>
  )
}
